import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import DashLayout from "@/components/DashLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { School, FileText, Calendar, Inbox, Users, Star, AlertTriangle, Trash2, ShieldCheck, Power } from "lucide-react";
import { toast } from "sonner";

export default function SuperDashboard() {
  const [stats, setStats] = useState({});
  const [demo, setDemo] = useState({ _total: 0 });
  const [attEdits, setAttEdits] = useState([]);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const load = () => {
    api.get("/stats/super").then((r) => setStats(r.data)).catch(() => {});
    api.get("/erp/demo/stats").then((r) => setDemo(r.data)).catch(() => {});
    api.get("/erp/attendance-edits?status=pending").then((r) => setAttEdits(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const purge = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/erp/demo/purge");
      toast.success(`Purged ${data.total} demo records`);
      setConfirming(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Purge failed");
    } finally { setBusy(false); }
  };

  const kpis = [
    { label: "Total Schools", value: stats.schools, icon: School, color: "text-blue-700 bg-blue-50" },
    { label: "Featured", value: stats.featured, icon: Star, color: "text-amber-700 bg-amber-50" },
    { label: "Blog Posts", value: stats.blogs, icon: FileText, color: "text-emerald-700 bg-emerald-50" },
    { label: "Events", value: stats.events, icon: Calendar, color: "text-purple-700 bg-purple-50" },
    { label: "New Submissions", value: stats.submissions_new, icon: Inbox, color: "text-orange-700 bg-orange-50" },
    { label: "Total Users", value: stats.users, icon: Users, color: "text-slate-700 bg-slate-100" },
  ];

  const topDemoBreakdown = Object.entries(demo)
    .filter(([k, v]) => k !== "_total" && v > 0)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8);

  return (
    <DashLayout variant="super" title="Dashboard">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {kpis.map((k) => (
          <Card key={k.label} className="p-5" data-testid={`kpi-${k.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className={`w-10 h-10 rounded ${k.color} flex items-center justify-center`}>
              <k.icon className="w-5 h-5" />
            </div>
            <div className="text-3xl font-black tracking-tight text-slate-900 mt-4">{k.value ?? "—"}</div>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mt-1">{k.label}</div>
          </Card>
        ))}
      </div>

      <Card className="p-5 mt-6 border-2 border-blue-200 bg-blue-50/30" data-testid="att-edits-card">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded bg-blue-100 flex items-center justify-center shrink-0">
            <ShieldCheck className="w-5 h-5 text-blue-700" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-slate-900">Attendance Edit Requests — {attEdits.length} pending</h3>
            <p className="text-sm text-slate-600 mt-1">
              Teachers cannot edit attendance directly — every change awaits your one-click approval here.
            </p>
            <div className="mt-3 space-y-2 max-h-72 overflow-y-auto">
              {attEdits.length === 0 && <div className="text-xs text-slate-400 py-2">No pending requests</div>}
              {attEdits.map((e) => (
                <div key={e.id} className="bg-white border border-blue-100 rounded p-3 flex items-center justify-between gap-2 text-sm" data-testid={`att-edit-${e.id}`}>
                  <div className="flex-1">
                    <div className="font-semibold">Class {e.class_name} · Student <code className="bg-slate-100 px-1 rounded">{e.student_id.slice(-6)}</code></div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {e.date} · <b className="text-red-600">{e.from_status}</b> → <b className="text-emerald-600">{e.to_status}</b>
                    </div>
                    <div className="text-xs text-slate-600 mt-1 italic">"{e.reason}"</div>
                    <div className="text-[10px] text-slate-400 mt-0.5">Requested by {e.requested_by} · {e.created_at?.slice(0, 16).replace("T", " ")}</div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700"
                      onClick={async () => {
                        try {
                          await api.post(`/erp/attendance-edits/${e.id}/approve?decision=approved`);
                          toast.success("Approved"); load();
                        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                      }}
                      data-testid={`att-approve-${e.id}`}
                    >Approve</Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        await api.post(`/erp/attendance-edits/${e.id}/approve?decision=rejected`);
                        toast.success("Rejected"); load();
                      }}
                      data-testid={`att-reject-${e.id}`}
                    >Reject</Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-5 mt-6 border-2 border-amber-200 bg-amber-50/30" data-testid="demo-purge-card">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded bg-amber-100 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-5 h-5 text-amber-700" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-slate-900">Demo Data Cleanup</h3>
            <p className="text-sm text-slate-600 mt-1">
              Remove all seeded demo records (marked <code className="bg-white px-1.5 py-0.5 rounded text-xs">demo: true</code>) across schools, users, students, drivers, buses, payroll and every ERP collection. Use this before go-live so real customer data is untouched.
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <div className="text-sm">
                Total demo records: <span className="font-bold text-slate-900 tabular-nums" data-testid="demo-total">{demo._total || 0}</span>
              </div>
              {topDemoBreakdown.length > 0 && (
                <div className="flex flex-wrap gap-2 text-xs">
                  {topDemoBreakdown.map(([k, v]) => (
                    <span key={k} className="bg-white border border-amber-200 rounded px-2 py-0.5">
                      {k}: <b>{v}</b>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="mt-4 flex gap-2">
              {!confirming ? (
                <Button
                  variant="outline"
                  className="border-red-300 text-red-700 hover:bg-red-50"
                  onClick={() => setConfirming(true)}
                  disabled={(demo._total || 0) === 0}
                  data-testid="demo-purge-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Purge {demo._total || 0} demo records
                </Button>
              ) : (
                <>
                  <Button
                    className="bg-red-600 hover:bg-red-700"
                    onClick={purge}
                    disabled={busy}
                    data-testid="demo-purge-confirm"
                  >
                    {busy ? "Purging…" : "Yes, delete permanently"}
                  </Button>
                  <Button variant="outline" onClick={() => setConfirming(false)} data-testid="demo-purge-cancel">
                    Cancel
                  </Button>
                </>
              )}
              <Button variant="ghost" size="sm" onClick={load} data-testid="demo-refresh">Refresh</Button>
            </div>
          </div>
        </div>
      </Card>
    </DashLayout>
  );
}
